// Keyboard-synced viewport for iOS.
//
// The Keyboard plugin's resize:'body' shrinks document.body, but every app
// shell here is sized in dvh — a viewport unit that never changes when only
// body is resized — so the keyboard slid OVER the composer instead of pushing
// it up. This module drives the shells directly: the moment keyboardWillShow
// fires (which is when the native animation starts), it stamps the keyboard
// height into a --kb custom property on <html> and adds a `kb-open` class.
// Shells sized with calc(100dvh - var(--kb, 0px)) plus a matching CSS
// transition then glide up in step with the keyboard, iMessage style.
//
// Phones and touch iPads both take the shell: the docked iPad keyboard covers
// the composer exactly like a phone keyboard does. Desktop, the Mac app and
// wide windows without touch never do (see shouldUseKeyboardViewport).
//
// iOS only: Android uses adjustResize, where the OS shrinks the webview
// itself and dvh already tracks the keyboard — subtracting --kb there would
// double-shrink the layout.

import { Capacitor } from "@capacitor/core";

export function keyboardInsetFromBodyStamp(viewportHeight: number, bodyHeight: number): number {
  if (!Number.isFinite(viewportHeight) || !Number.isFinite(bodyHeight)) return 0;
  const keyboardHeight = Math.round(viewportHeight - bodyHeight);
  return keyboardHeight > 80 && keyboardHeight < viewportHeight * 0.75 ? keyboardHeight : 0;
}

export function shouldUseKeyboardViewport(
  viewportWidth: number,
  hasFinePointer = false,
  viewportHeight = Infinity,
  screenShortSide = Infinity,
  touchTablet = false,
): boolean {
  // A Mac running the iOS TestFlight build can report a narrow CSS viewport
  // after zooming or resizing even though no software keyboard covers the
  // WebView. Width alone therefore misclassified the desktop as a phone and
  // left a false keyboard inset across the message composer. A precise
  // pointer is authoritative desktop evidence: never run the keyboard shell
  // on that surface.
  if (!Number.isFinite(viewportWidth) || hasFinePointer) return false;
  // A phone retains a short physical screen dimension when rotated.
  const rotatedPhone = viewportHeight < 600 && screenShortSide > 0 && screenShortSide < 600;
  if (viewportWidth < 768 || rotatedPhone) return true;
  // Wide windows only take the shell on a touch iPad (see isTouchTablet).
  // The docked software keyboard covers the bottom of the screen there just
  // as it does on a phone. A hardware or Magic Keyboard, a floating or split
  // keyboard and a Stage Manager window that the keyboard does not reach all
  // report no usable height, and the event and body-stamp gates below turn
  // that into a zero inset, so the shell stays full height.
  return touchTablet;
}

/**
 * Touch iPad evidence for the keyboard shell: touch points, a coarse primary
 * pointer, and not the iOS build running on a Mac. A Mac never reports touch
 * points, and an iPad with a trackpad attached reports a fine pointer, which
 * already turns the shell off above.
 */
export function isTouchTablet(input: {
  maxTouchPoints: number;
  coarsePointer: boolean;
  macDesktopApp: boolean;
}): boolean {
  return !input.macDesktopApp && input.maxTouchPoints > 0 && input.coarsePointer;
}

/** Live read of shouldUseKeyboardViewport for this window. */
export function keyboardViewportActive(): boolean {
  if (typeof window === "undefined") return false;
  const media = (query: string) => window.matchMedia?.(query).matches ?? false;
  const root = document.documentElement;
  return shouldUseKeyboardViewport(
    window.innerWidth,
    media("(hover: hover) and (pointer: fine)"),
    window.innerHeight,
    Math.min(window.screen?.width ?? Infinity, window.screen?.height ?? Infinity),
    isTouchTablet({
      maxTouchPoints: navigator.maxTouchPoints ?? 0,
      coarsePointer: media("(pointer: coarse)"),
      macDesktopApp: root.dataset.macDesktopApp === "true",
    }),
  );
}

export function shouldClearWideViewportBodyHeight(
  viewportWidth: number,
  inlineBodyHeight: string,
  hasFinePointer = false,
  viewportHeight = Infinity,
  screenShortSide = Infinity,
  touchTablet = false,
): boolean {
  return !shouldUseKeyboardViewport(viewportWidth, hasFinePointer, viewportHeight, screenShortSide, touchTablet)
    && inlineBodyHeight.trim().length > 0;
}

export function keyboardInsetFromEvent(
  reportedHeight: number,
  learnedOffset: number,
  viewportHeight: number,
): number {
  if (!Number.isFinite(reportedHeight) || !Number.isFinite(viewportHeight)) return 0;
  // iOS can emit a zero-height "show" notification when a hardware keyboard is
  // connected. Never add the learned software-keyboard offset to that event:
  // doing so creates a fake inset and exposes the native black background.
  if (reportedHeight <= 80 || reportedHeight >= viewportHeight * 0.75) return 0;
  const safeOffset = Number.isFinite(learnedOffset) && learnedOffset >= 0 && learnedOffset <= 240
    ? learnedOffset
    : 0;
  const resolved = Math.round(reportedHeight + safeOffset);
  return resolved > 80 && resolved < viewportHeight * 0.75 ? resolved : 0;
}

/**
 * iPad only (wide windows): the plugin caches the iPad keyboard overlap until
 * the keyboard hides, so a rotation that skips the hide can leave a body stamp
 * written for the other orientation. When the visual viewport reports a real,
 * clearly different keyboard, trust the visual viewport. Phones keep the body
 * stamp as the authority (their event and stamp issues are documented below).
 */
export function reconcileTabletBodyInset(bodyInset: number, visualViewportInset: number, viewportWidth: number): number {
  if (viewportWidth < 768) return bodyInset;
  if (bodyInset > 80 && visualViewportInset > 80 && Math.abs(bodyInset - visualViewportInset) > 120) {
    return visualViewportInset;
  }
  return bodyInset;
}

export function shouldClearStaleKeyboardInset(
  currentInset: number,
  bodyInset: number,
  visualViewportInset: number,
  millisecondsSinceGeometry: number,
  holdActive: boolean,
): boolean {
  return currentInset > 0
    && bodyInset <= 80
    && visualViewportInset <= 80
    && !holdActive
    && millisecondsSinceGeometry >= 1200;
}

export function initKeyboardViewport() {
  if (Capacitor.getPlatform() !== "ios" || !Capacitor.isPluginAvailable("Keyboard")) return;
  if (document.documentElement.dataset.macDesktopApp === "true") {
    document.documentElement.style.setProperty("--kb", "0px");
    document.documentElement.classList.remove("kb-open");
    document.body?.style.removeProperty("height");
    return;
  }

  void (async () => {
    const { Keyboard } = await import("@capacitor/keyboard");
    const root = document.documentElement;
    const isCompactViewport = keyboardViewportActive;
    const isKeyboardSurface = (element: Element | null) => Boolean(
      element?.closest(".kb-shell, [data-keyboard-surface]"),
    );
    const isEditableControl = (element: HTMLElement | null): boolean => Boolean(
      element && (
        element.tagName === "INPUT"
        || element.tagName === "TEXTAREA"
        || element.isContentEditable
      ),
    );
    const currentInset = () => {
      const raw = parseInt(root.style.getPropertyValue("--kb"), 10);
      return Number.isFinite(raw) ? Math.max(0, raw) : 0;
    };
    const revealFocusedControl = () => {
      const active = document.activeElement as HTMLElement | null;
      if (!active) return;
      // Some portaled overlays size themselves directly from visualViewport.
      // Moving the document to reveal a field inside one of those overlays
      // fights the overlay's own keyboard-safe layout and can push its header
      // under the iOS status bar. Those controls opt out and remain anchored
      // by their local viewport sheet instead.
      if (active.closest('[data-keyboard-reveal="local"]')) return;
      if (isKeyboardSurface(active)) {
        active.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
        return;
      }
      // Screens that never opted into a kb-shell (full-screen flows, portaled
      // sheets, community composers) still must not leave the focused field
      // buried under the keys: when it sits below the visible band, center it.
      if (!isEditableControl(active)) return;
      const visibleBottom = window.innerHeight - currentInset();
      const rect = active.getBoundingClientRect();
      if (rect.bottom > visibleBottom - 16 || rect.top < 0) {
        active.scrollIntoView({ block: "center", inline: "nearest", behavior: "smooth" });
      }
    };

    // Tapping into a lower field while the keyboard is ALREADY up fires no
    // keyboard event at all, so the reveal above never ran — the field stayed
    // hidden behind the keys until the user scrolled by hand. Reveal on every
    // focus change while the keyboard is open.
    document.addEventListener("focusin", () => {
      if (!root.classList.contains("kb-open")) return;
      window.setTimeout(revealFocusedControl, 250);
    });

    // WKWebView scrolls the whole document to "reveal" the focused input the
    // moment the keyboard appears — on top of our shell shrink, that leaves a
    // dead band between the composer and the keyboard. Inside a kb-shell the
    // shell already keeps the input visible, so any document scroll is wrong:
    // pin it to zero for the whole time the keyboard is up. Screens outside a
    // kb-shell (login, public pages) keep the native scroll-into-view.
    let unpin: (() => void) | null = null;
    const resetScroll = () => {
      if (window.scrollY !== 0) window.scrollTo(0, 0);
      const doc = document.documentElement;
      if (doc.scrollTop !== 0) doc.scrollTop = 0;
      if (document.body.scrollTop !== 0) document.body.scrollTop = 0;
    };
    const pinScroll = () => {
      if (unpin) return;
      // Fight the native scroll for the whole show animation…
      let raf = 0;
      const until = performance.now() + 700;
      const loop = () => {
        resetScroll();
        if (performance.now() < until) raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);
      // …then keep the document pinned for as long as the keyboard stays up.
      window.addEventListener("scroll", resetScroll, { passive: true });
      unpin = () => {
        cancelAnimationFrame(raf);
        window.removeEventListener("scroll", resetScroll);
        unpin = null;
      };
    };

    const restoreWideViewport = () => {
      root.style.setProperty("--kb", "0px");
      root.classList.remove("kb-open");
      unpin?.();
    };
    const open = (height: number) => {
      if (!isCompactViewport()) {
        restoreWideViewport();
        return;
      }
      root.style.setProperty("--kb", `${Math.max(0, Math.round(height))}px`);
      root.classList.add("kb-open");
      const active = document.activeElement;
      if (isKeyboardSurface(active) && !active?.closest(".kb-natural-scroll")) pinScroll();
      // Portaled sheets live under <body>, outside the runner's .kb-shell.
      // Reveal their focused control after the inset lands so search, sets,
      // and reps fields remain above the software keyboard.
      window.setTimeout(revealFocusedControl, 320);
    };
    const close = () => {
      root.style.setProperty("--kb", "0px");
      root.classList.remove("kb-open");
      // `resize: body` is native-plugin state, not application layout. If a
      // hide callback races or the plugin leaves its inline height behind,
      // the WebView otherwise remains permanently shorter and exposes a dead
      // strip below the composer. Clearing is idempotent and the next genuine
      // show event/body stamp will restore the measured keyboard geometry.
      document.body?.style.removeProperty("height");
      unpin?.();
      resetScroll();
    };

    // A plugin event is the animated first-response signal — when one lands
    // with a NEW height, give the native side time to finish (keyboard
    // animation + the plugin's post-animation body stamp) before the
    // corrector may disagree. Only height CHANGES refresh the hold: iOS
    // betas re-fire show events with the same (sometimes wrong) height over
    // and over, and refreshing on every repeat starved the corrector
    // forever — seen in the field as kb:251px vs a real 347px keyboard,
    // composer parked 96px under the keys.
    let holdUntil = 0;
    let lastEventHeight = -1;
    let lastPositiveGeometryAt = 0;
    const holdFor = (ms: number) => {
      holdUntil = Math.max(holdUntil, performance.now() + ms);
    };
    // The event under-reports the keyboard height on some iOS versions (by
    // exactly the screen/viewport inset — 251 reported vs 347 real in the
    // field), which made the shell rise short and visibly snap the missing
    // 96px once the native body stamp corrected it. Once that correction
    // happens we LEARN the offset, persist it, and apply it to every later
    // event — so the very first glide already lands on the keyboard's edge.
    const OFFSET_KEY = "kb-event-offset-v2";
    let eventOffset = 0;
    try {
      const saved = parseInt(localStorage.getItem(OFFSET_KEY) ?? "", 10);
      if (Number.isFinite(saved) && saved >= 0 && saved <= 240) eventOffset = saved;
    } catch {
      /* private mode */
    }
    const learnOffset = (trueKb: number) => {
      if (lastEventHeight <= 0) return;
      const offset = trueKb - lastEventHeight;
      if (offset >= 0 && offset <= 240 && offset !== eventOffset) {
        eventOffset = offset;
        try {
          localStorage.setItem(OFFSET_KEY, String(offset));
        } catch {
          /* private mode */
        }
      }
    };
    const onShowEvent = (height: number) => {
      const resolvedHeight = keyboardInsetFromEvent(height, eventOffset, window.innerHeight);
      if (resolvedHeight === 0) {
        // Hardware keyboards and stale native callbacks can report 0 while the
        // field remains focused. Focus alone is not proof that a software
        // keyboard occupies screen space, so leave the shell untouched.
        return;
      }
      if (height !== lastEventHeight) {
        lastEventHeight = height;
        holdFor(900);
      }
      lastPositiveGeometryAt = performance.now();
      open(resolvedHeight);
    };
    const onHideEvent = () => {
      lastEventHeight = -1;
      holdFor(900);
      close();
    };

    await Keyboard.addListener("keyboardWillShow", (info) => onShowEvent(info.keyboardHeight));
    // Height can change while open (emoji keyboard, iPad split, QuickType).
    await Keyboard.addListener("keyboardDidShow", (info) => onShowEvent(info.keyboardHeight));
    await Keyboard.addListener("keyboardWillHide", onHideEvent);
    await Keyboard.addListener("keyboardDidHide", onHideEvent);

    // Redundant show channel: the plugin dispatches the same native
    // notifications as window events too. If the plugin-listener delivery
    // ever drops one, this path usually still lands. Shows only — hide stays
    // single-channel through the plugin listeners above.
    const windowShow = (ev: Event) => {
      const h = (ev as unknown as { keyboardHeight?: number }).keyboardHeight;
      if (typeof h === "number" && h > 0) onShowEvent(h);
    };
    window.addEventListener("keyboardWillShow", windowShow);
    window.addEventListener("keyboardDidShow", windowShow);

    // ── Self-healing corrector ───────────────────────────────────────────
    // The plugin's EVENT height cannot be trusted on its own: iOS betas
    // under-report it (field reading: event said 251px, keyboard was 347px,
    // composer parked 96px under the keys). The one gauge that has been
    // right every time is document.body's inline pixel height — the plugin
    // stamps (screen - keyboard) onto body from NATIVE code after every
    // show and clears it after every hide. When that stamp exists it is
    // AUTHORITATIVE: it overrides the event height immediately, in both
    // directions, regardless of hold. It only appears after the animation
    // settles, so it never fights one.
    const editableFocused = () => {
      const ae = document.activeElement as HTMLElement | null;
      return !!ae?.matches?.("input, textarea, [contenteditable='true']") && isKeyboardSurface(ae);
    };
    // Invert the plugin's native body stamp against the app viewport. Native
    // TestFlight uses viewport-fit=cover, so physical screen height includes
    // non-app chrome and creates the exact dead band seen above the keyboard.
    const kbFromBodyStamp = () => {
      const bodyPx = parseFloat(document.body.style.height);
      if (!Number.isFinite(bodyPx)) return 0;
      return keyboardInsetFromBodyStamp(window.innerHeight, bodyPx);
    };
    const syncNow = () => {
      // Desktop, the Mac app and wide non-touch windows never need the shell
      // shrink. Capacitor can leave a stale inline body height after a hardware,
      // QuickType, or floating-keyboard event. On the Mac TestFlight app that
      // stamp can be only 40-50px, so the old >80px keyboard threshold missed
      // it and exposed the native black background over the composer. No
      // screen outside the keyboard shell uses an intentional inline body
      // height: clear every plugin-written stamp and restore the full shell.
      if (!isCompactViewport()) {
        if (document.body.style.height.trim().length > 0) {
          document.body.style.removeProperty("height");
        }
        restoreWideViewport();
        return;
      }
      const current = parseInt(root.style.getPropertyValue("--kb"), 10) || 0;
      const vv = window.visualViewport;
      const vvInset = vv ? Math.round(window.innerHeight - vv.height - vv.offsetTop) : 0;
      const bodyInset = reconcileTabletBodyInset(kbFromBodyStamp(), vvInset, window.innerWidth);
      // Field diagnostics surfaced by KbDebugBadge: proves whether the
      // corrector is running and what it computed at the moment of a
      // screenshot. Cheap string, updated on every pass.
      const w = window as unknown as { __kbSync?: string; __kbSyncRuns?: number };
      w.__kbSyncRuns = (w.__kbSyncRuns ?? 0) + 1;
      w.__kbSync = `${w.__kbSyncRuns}@${bodyInset}`;
      if (bodyInset > 80) {
        lastPositiveGeometryAt = performance.now();
        learnOffset(bodyInset);
        if (Math.abs(bodyInset - current) > 8) open(bodyInset);
        return;
      }
      if (performance.now() < holdUntil) return;
      if (vvInset > 80) {
        lastPositiveGeometryAt = performance.now();
        // Raise-only backup when the body stamp is absent. Real when
        // nonzero; a zero from the visual viewport means nothing.
        if (Math.abs(vvInset - current) > 24) open(vvInset);
      } else if (current !== 0 || root.classList.contains("kb-open")) {
        // Both independent geometry gauges read closed. A focused field is not
        // enough to keep an inset alive: Mac Catalyst/iPhone hardware keyboards
        // retain focus without covering the app, and missed hide events used to
        // leave the shell permanently short. Give real show geometry a brief
        // settle window, then clear any inset that no gauge can corroborate.
        if (shouldClearStaleKeyboardInset(
          current,
          bodyInset,
          vvInset,
          performance.now() - lastPositiveGeometryAt,
          performance.now() < holdUntil,
        )) close();
      }
    };
    // The body stamp landing IS the moment of truth — react the instant the
    // plugin writes it instead of waiting for the next poll.
    new MutationObserver(syncNow).observe(document.body, { attributes: true, attributeFilter: ["style"] });
    window.visualViewport?.addEventListener("resize", syncNow);
    window.addEventListener("resize", syncNow);
    // Re-check shortly after focusing a field (missed show hurts most here)…
    document.addEventListener(
      "focusin",
      (e) => {
        if (!isKeyboardSurface(e.target as HTMLElement | null)) return;
        window.setTimeout(revealFocusedControl, 320);
        window.setTimeout(syncNow, 1000);
      },
      true,
    );
    document.addEventListener(
      "focusout",
      () => {
        window.setTimeout(() => {
          if (!editableFocused()) syncNow();
        }, 80);
      },
      true,
    );
    // …and when the app returns to the foreground (notification deep links)…
    document.addEventListener("visibilitychange", () => {
      if (!document.hidden) window.setTimeout(syncNow, 1000);
    });
    // Rotation while the keyboard is up: the keyboard changes height (and the
    // iPad plugin may re-send the old one), the window changes shape, and
    // the answer to "does this window take the shell" can flip. Drop the
    // hold so the corrector can adopt the new body stamp or visual viewport
    // reading as soon as it lands, and re-check once the rotation settles.
    let lastWidth = window.innerWidth;
    const onRotate = () => {
      if (window.innerWidth === lastWidth) return;
      lastWidth = window.innerWidth;
      holdUntil = 0;
      lastEventHeight = -1;
      lastPositiveGeometryAt = performance.now();
      syncNow();
      window.setTimeout(syncNow, 400);
      window.setTimeout(() => {
        syncNow();
        if (root.classList.contains("kb-open")) revealFocusedControl();
      }, 1000);
    };
    window.addEventListener("resize", onRotate);
    window.addEventListener("orientationchange", onRotate);
    // Clear a stamp that was written before the listeners above attached.
    syncNow();
    // …and continuously while the keyboard is believed open OR a text field
    // inside a kb-shell holds focus. Gating on focus alone left the
    // corrector dead when a long-press (copy, reactions) stole focus while
    // the keyboard stayed up — seen in the field as kb stuck at the event's
    // under-reported height with the composer buried.
    window.setInterval(() => {
      if (!root.classList.contains("kb-open")) {
        const ae = document.activeElement as HTMLElement | null;
        if (!ae?.matches?.("input, textarea, [contenteditable='true']")) return;
        if (!isKeyboardSurface(ae)) return;
      }
      syncNow();
    }, 600);
  })();
}
